// Removed XLSX import, not needed for CSV export
import moment from "moment";
import { toast } from "react-toastify";

/**
 * Export data to Excel file
 * @param {Array} data - Array of objects to export
 * @param {Object} config - Configuration object
 * @param {string} config.filename - Base filename (without extension)
 * @param {string} config.sheetName - Name of the Excel sheet
 * @param {Object} config.columnMapping - Object mapping data keys to display headers
 * @param {boolean} config.autoFitColumns - Whether to auto-fit column widths
 * @param {boolean} config.showToast - Whether to show success/error toast messages
 * @param {number} config.maxColumnWidth - Maximum column width
 * @returns {boolean} - Success status
 */
export const exportToCSV = (data, config = {}) => {
    const {
        filename = 'export',
        columnMapping = null,
        showToast = true,
        delimiter = ',',
    } = config;

    try {
        if (!data || !Array.isArray(data) || data.length === 0) {
            if (showToast) toast.warning("No data available to export");
            return false;
        }

        // Prepare data for CSV export
        let csvRows = [];
        let headers = [];
        if (columnMapping) {
            headers = Object.values(columnMapping);
            csvRows.push(headers.join(delimiter));
            data.forEach(row => {
                const rowData = Object.keys(columnMapping).map(key => {
                    let cell = row[key] !== undefined ? row[key] : '';
                    // Escape quotes and delimiters
                    if (typeof cell === 'string') {
                        cell = cell.replace(/"/g, '""');
                        if (cell.includes(delimiter) || cell.includes('"') || cell.includes('\n')) {
                            cell = `"${cell}"`;
                        }
                    }
                    return cell;
                });
                csvRows.push(rowData.join(delimiter));
            });
        } else {
            if (data.length > 0) {
                headers = Object.keys(data[0]);
                csvRows.push(headers.join(delimiter));
                data.forEach(row => {
                    const rowData = headers.map(key => {
                        let cell = row[key] !== undefined ? row[key] : '';
                        if (typeof cell === 'string') {
                            cell = cell.replace(/"/g, '""');
                            if (cell.includes(delimiter) || cell.includes('"') || cell.includes('\n')) {
                                cell = `"${cell}"`;
                            }
                        }
                        return cell;
                    });
                    csvRows.push(rowData.join(delimiter));
                });
            }
        }

        // Generate filename with timestamp
        const timestamp = moment().format('lll').replace(/:/g, '-');
        const finalFilename = `${filename}_${timestamp}.csv`;

        // Create CSV file and trigger download
        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', finalFilename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (showToast) toast.success(`CSV export completed successfully! File: ${finalFilename}`);
        return true;
    } catch (error) {
        console.error("CSV export error:", error);
        if (showToast) toast.error(`Failed to export CSV file: ${error.message}`);
        return false;
    }
};

/**
 * Export authentication history data to Excel
 * @param {Array} data - Authentication history data array
 * @param {Object} columnMapping - Column mapping for the export
 * @param {Object} customConfig - Custom configuration to override defaults
 * @returns {boolean} - Success status
 */

export const exportReportToCSV = (data, columnMapping, customConfig = {}) => {
    const defaultConfig = {
        filename: data?.name || 'report_export',
        columnMapping,
        showToast: true,
    };

    const config = { ...defaultConfig, ...customConfig };
    return exportToCSV(data?.list, config);
};
